import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { SuggestionOptions } from '@tiptap/suggestion';
import type { AutocompleteProvider } from '../../types';
import { HandlebarsTagView } from './HandlebarsTagView';

export interface HandlebarsNodeOptions {
  HTMLAttributes: Record<string, unknown>;
  suggestion: Partial<SuggestionOptions>;
  autocompleteProvider?: AutocompleteProvider;
  showDescriptions?: boolean;
  allowFreeformHandlebars?: boolean;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    handlebarsTag: {
      insertHandlebarsTag: (attrs: {
        variable: string;
        description?: string | null;
        isValid?: boolean;
        error?: string | null;
      }) => ReturnType;
    };
  }
}

export const HandlebarsNode = Node.create<HandlebarsNodeOptions>({
  name: 'handlebarsTag',

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      suggestion: {},
      autocompleteProvider: undefined,
      showDescriptions: false,
      allowFreeformHandlebars: false,
    };
  },

  addAttributes() {
    return {
      variable: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-variable') || '',
        renderHTML: (attributes) => ({
          'data-variable': attributes.variable,
        }),
      },
      description: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-description') || null,
        renderHTML: (attributes) => {
          if (!attributes.description) return {};
          return { 'data-description': attributes.description };
        },
      },
      isValid: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-valid') !== 'false',
        renderHTML: (attributes) => ({
          'data-valid': attributes.isValid ? 'true' : 'false',
        }),
      },
      error: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-error') || null,
        renderHTML: (attributes) => {
          if (!attributes.error) return {};
          return { 'data-error': attributes.error };
        },
      },
      nonEditable: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-non-editable') === 'true',
        renderHTML: (attributes) => {
          if (!attributes.nonEditable) return {};
          return { 'data-non-editable': 'true' };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-handlebars]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-handlebars': '' },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      `{{${HTMLAttributes.variable}}}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HandlebarsTagView, {
      className: 'handlebars-tag-wrapper',
    });
  },

  addCommands() {
    return {
      insertHandlebarsTag:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              variable: attrs.variable,
              description: attrs.description ?? null,
              isValid: attrs.isValid ?? true,
              error: attrs.error ?? null,
            },
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isHandlebarsTag = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isHandlebarsTag = true;
              tr.delete(pos, pos + node.nodeSize);
              return false;
            }
          });

          return isHandlebarsTag;
        }),
    };
  },
});

export default HandlebarsNode;
